import { useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Form } from "rsuite";
import { Button } from "../../../components/button.component";
import { Card } from "../../../components/card.component";
import { AppRouteUi } from "../../../config/appRoute";
import { InvoiceCards } from "../../../features/invoice/invoiceCards.component";
import {
  ApiSchemaInvoice,
  ApiSchemaTaskDate,
} from "../../../models/api/invoice";
import { UtilityDate } from "../../../utils/dateUtils";
import { ApiInvoice } from "../../../core/apis/invoice";
import { showErrorToast } from "../../../components/toast.component";

export const PageInvoiceEdit = () => {
  const modifiedInvoiceRef = useRef<Map<string, ApiSchemaTaskDate>>(new Map());
  const routeHistory = useHistory<ApiSchemaInvoice>();
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const [invoice, setInvoice] = useState<ApiSchemaInvoice>();
  const [invoiceTasks, setInvoiceTasks] = useState<ApiSchemaTaskDate[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const getInvoiceTask = async () => {
      const response = await ApiInvoice.GetTask(invoiceId)!;
      if (response?.isSuccess) {
        setInvoiceTasks(response.data!);
      } else {
        response && showErrorToast({ message: response?.message });
      }
    };

    getInvoiceTask();
  }, [invoiceId]);

  useEffect(() => {
    if (
      !routeHistory.location.state ||
      routeHistory.location.state.sKey !== invoiceId
    )
      routeHistory.replace(AppRouteUi.Invoice.List());
    setInvoice(routeHistory.location.state);
  }, [routeHistory, invoiceId]);

  const handleInvoiceClick = (invoiceTask: ApiSchemaTaskDate) => {
    setInvoiceTasks(
      invoiceTasks.map((task) => {
        if (
          task.appointmentId === invoiceTask.appointmentId &&
          task.date === invoiceTask.date
        ) {
          if (JSON.stringify(task) !== JSON.stringify(invoiceTask)) {
            modifiedInvoiceRef.current.set(
              invoiceTask.invoiceTaskId!,
              invoiceTask
            );
          }
          return invoiceTask;
        } else {
          return task;
        }
      })
    );
  };

  const handleSave = async () => {
    setIsLoading(true);
    const modifiedInvoice = Array.from(modifiedInvoiceRef.current.values());
    const response = await ApiInvoice.UpdateInvoice({
      invoiceId,
      tasksDates: modifiedInvoice,
    });
    if (response.isSuccess) {
      routeHistory.push(AppRouteUi.Invoice.ById(invoiceId), {
        ...invoice,
        taskDates: response.data?.taskDates,
      } as ApiSchemaInvoice);
    } else {
      showErrorToast({ message: response.message });
    }
    setIsLoading(false);
  };

  return (
    <Card
      header={
        <div className="row align-items-center justify-content-between bg-light py-2">
          <div className="col content-box-title">
            <div className="d-flex align-items-center">
              <div
                onClick={() => {
                  routeHistory.length > 1
                    ? routeHistory.goBack()
                    : routeHistory.push(AppRouteUi.Root);
                }}
                className="pe-2 border-2 border-end click text-green-200"
              >
                <i className="fas fa-arrow-left text-green-200" />
              </div>
              <h4 className="text-capitalize mb-0 ps-2 text-green-200 fw-bold">
                Edit {invoice?.invoiceNumber}
              </h4>
            </div>
          </div>
        </div>
      }
    >
      <Form className="row" fluid>
        <div className="col-md-10 col-12 border-end px-4 add-page-ctl">
          <div className="card shadow-sm mb-4">
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-4">
                  <div className="p-3 bg-light rounded">
                    <h6 className="text-muted mb-2">Patient's Name</h6>
                    <p className="h5 mb-0">
                      {invoice?.patientFirstName + " " + invoice?.patientLastName}
                    </p>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="p-3 bg-light rounded">
                    <h6 className="text-muted mb-2">Care giver's Name</h6>
                    <p className="h5 mb-0">
                      {invoice?.caregiverFirstName + " " + invoice?.caregiverLastName}
                    </p>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="p-3 bg-light rounded">
                    <h6 className="text-muted mb-2">Total Amount</h6>
                    <p className="h5 mb-0">
                      {invoice?.totalAmount}
                    </p>
                  </div>
                </div>
              </div>

              <div className="row g-3 mt-2">
                <div className="col-md-4">
                  <div className="p-3 bg-light rounded">
                    <h6 className="text-muted mb-2">Start Date</h6>
                    <p className="h5 mb-0">
                      {UtilityDate.formatHumanFriendlyDate(
                        invoice?.startDate
                      )}
                    </p>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="p-3 bg-light rounded">
                    <h6 className="text-muted mb-2">End Date</h6>
                    <p className="h5 mb-0">
                      {UtilityDate.formatHumanFriendlyDate(
                        invoice?.endDate
                      )}
                    </p>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="p-3 bg-light rounded">
                    <h6 className="text-muted mb-2">Total Days</h6>
                    <p className="h5 mb-0">
                      {invoice?.startDate && invoice?.endDate
                        ? Math.ceil(
                            (new Date(invoice.endDate).getTime() -
                              new Date(invoice.startDate).getTime()) /
                              (1000 * 60 * 60 * 24)
                          ) 
                        : 0}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="card shadow-sm">
            <div className="card-body">
              <h5 className="card-title d-flex justify-content-between align-items-center">
                <span>Task Dates</span>
                <span className="badge bg-success rounded-pill">{invoiceTasks.length} tasks</span>
              </h5>
              <InvoiceCards
                invoices={invoiceTasks}
                handleInvoiceClick={handleInvoiceClick}
                isEditing={true}
              />
            </div>
          </div>
        </div>

        <div className="col-md-2 col-12 pt-3">
          <Button
            className="m-1 w-100 btn-green btn-lg mb-3"
            type="submit"
            name="Save"
            loading={isLoading}
            onClick={handleSave}
            disabled={!invoiceTasks.length && !modifiedInvoiceRef.current.size}
          />
          <Button
            onClick={() => {
              routeHistory.length > 1
                ? routeHistory.goBack()
                : routeHistory.push(AppRouteUi.Root);
            }}
            type="button"
            className="m-1 w-100 btn-green-subtle"
            name="Return without saving"
          />
        </div>
      </Form>
    </Card>
  );
};
